import { PreloadedQuery } from 'react-relay';

import { formatIsoLocalDateTime } from '@townsquare/date-utils/format';
import {
	getUtcEndOfGoalMonth,
	getUtcEndOfWeekDate,
	parseGoalUpdatesFeedStartDate,
	parseProjectUpdatesFeedStartDate,
} from '@townsquare/date-utils/update';
import {
	convertQueryParamToUpdateStateFilter,
	getValidUpdateQueryParamFilter,
} from '@townsquare/home-view/query-param-filter';
import { createRelayResource } from '@townsquare/relay-utils';
import { RouteResource } from '@townsquare/router/routing-types';
import type { ResourceContext } from '@townsquare/router/types';

import ReadingModeFullScreenQuery, {
	type ReadingModeFullScreenQuery as ReadingModeFullScreenQueryType,
} from '../ui/__generated__/ReadingModeFullScreenQuery.graphql';

const PLACEHOLDER_UUID = 'dac6d157-4ea3-4208-be35-d2a839d6051d';

export const readingModeResource: RouteResource<
	PreloadedQuery<ReadingModeFullScreenQueryType, Record<string, unknown>>
> = createRelayResource<ReadingModeFullScreenQueryType>({
	getQuery: (routerContext, resourceContext) => {
		const context = resourceContext as ResourceContext;
		const updateType = routerContext.match.params.updateType ?? '';
		const isProjectUpdates = updateType === 'projects';
		const isGoalUpdates = updateType === 'goals';
		const isTeamUpdates = updateType === 'team';
		const isTagUpdates = updateType === 'tag';
		const startQueryParam = routerContext.query.start;
		const teamId = routerContext.query.teamId;
		const tagUuid = routerContext.query.id;

		let startDate;
		let endDate;

		if (isProjectUpdates) {
			startDate = parseProjectUpdatesFeedStartDate(startQueryParam);
			endDate = getUtcEndOfWeekDate(startDate);
		} else {
			startDate = parseGoalUpdatesFeedStartDate(startQueryParam);
			endDate = getUtcEndOfGoalMonth(startDate);
		}

		const filter = routerContext.query.filter
			? convertQueryParamToUpdateStateFilter(
					getValidUpdateQueryParamFilter(routerContext.query.filter),
				)
			: undefined;

		return {
			query: ReadingModeFullScreenQuery,
			variables: {
				isProjectUpdates: isProjectUpdates,
				isGoalUpdates: isGoalUpdates,
				isTeamUpdates: isTeamUpdates,
				isTagUpdates: isTagUpdates,
				stateFilter: filter,
				createdAfter: formatIsoLocalDateTime(startDate),
				createdBefore: formatIsoLocalDateTime(endDate),
				workspaceUUID: context.workspace?.UUID ?? '',
				teamId: teamId ?? 'fake-team-id',
				tagUuid: tagUuid ?? PLACEHOLDER_UUID,
			},
			options: {
				fetchPolicy: 'store-and-network',
			},
		};
	},
});
